// src/pages/finance-portal/inventory/ReceiveForm.js
//
// Scan-or-enter receiving with inspection and lot/serial capture.
// Can be opened from a PO ("Receive Goods" button) or standalone.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Alert, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
    FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem,
    Paper, Select, Stack, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axiosInstance from '../../../utils/axiosInstance';
import {
    FONT_FAMILY, INV_ACCENT, INV_ACCENT_DARK, formatCurrency, formatDate,
    RECEIPT_STATUS_COLORS, INSPECTION_COLORS, statusChip,
} from './components/inventoryFormatters';

const INSPECTION_RESULTS = ['NOT_INSPECTED', 'PASS', 'FAIL', 'HOLD'];

export default function ReceiveForm() {
    const { receiptId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const editing = Boolean(receiptId);
    const preselectedPO = searchParams.get('po');

    const [receipt, setReceipt] = useState(null);
    const [loading, setLoading] = useState(editing);
    const [saving, setSaving] = useState(false);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);

    const [vendors, setVendors] = useState([]);
    const [locations, setLocations] = useState([]);
    const [pos, setPOs] = useState([]);

    const [form, setForm] = useState({
        purchase_order_id: preselectedPO ? Number(preselectedPO) : null,
        vendor_id: null, received_at_location_id: null,
        quarantine_location_id: null,
        receipt_date: new Date().toISOString().slice(0, 10),
        vendor_packing_slip_number: '', vendor_invoice_number: '',
        carrier: '', tracking_number: '',
        notes: '', currency: 'USD',
        lines: [],
    });

    const load = useCallback(async () => {
        if (!editing) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/inventory/receipts/${receiptId}`);
            if (res.data?.success) {
                setReceipt(res.data.data);
                setForm((f) => ({ ...f, ...res.data.data, lines: res.data.data.lines || [] }));
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load');
        } finally {
            setLoading(false);
        }
    }, [editing, receiptId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        axiosInstance.get('/api/finance/vendors', { params: { per_page: 200 } })
            .then((r) => setVendors(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/inventory/locations')
            .then((r) => setLocations(r.data?.data || [])).catch(() => {});
        axiosInstance.get('/api/finance/inventory/purchase-orders',
            { params: { status: 'APPROVED', per_page: 100 } })
            .then((r) => setPOs(r.data?.data || [])).catch(() => {});
    }, []);

    // When a PO is preselected, fetch its details to pre-fill lines
    useEffect(() => {
        if (form.purchase_order_id && !editing && form.lines.length === 0) {
            axiosInstance.get(`/api/finance/inventory/purchase-orders/${form.purchase_order_id}`)
                .then((r) => {
                    const po = r.data?.data;
                    if (!po) return;
                    setForm((f) => ({
                        ...f,
                        vendor_id: po.vendor_id,
                        received_at_location_id: po.ship_to_location_id || null,
                        currency: po.currency,
                        lines: (po.lines || []).filter((l) => (l.ordered_qty || 0) - (l.received_qty || 0) > 0)
                            .map((l) => ({
                                po_line_id: l.id,
                                product_id: l.product_id,
                                product_name: l.product_name || l.description,
                                sku: l.sku,
                                expected_qty: (l.ordered_qty || 0) - (l.received_qty || 0),
                                received_qty: (l.ordered_qty || 0) - (l.received_qty || 0),
                                accepted_qty: (l.ordered_qty || 0) - (l.received_qty || 0),
                                rejected_qty: 0, on_hold_qty: 0,
                                unit_cost: l.unit_price || 0,
                                uom: l.uom || 'EA',
                                inspection_result: 'NOT_INSPECTED',
                                lot_number: '', manufactured_date: '', expiration_date: '',
                                serial_numbers: [],
                            })),
                    }));
                });
        }
    }, [form.purchase_order_id, editing, form.lines.length]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const updateLine = (i, k, v) => {
        const next = form.lines.slice();
        next[i] = { ...next[i], [k]: v };
        setForm((f) => ({ ...f, lines: next }));
    };

    const handleSave = async () => {
        setSaving(true); setError(null);
        try {
            if (editing) {
                setInfo('Receipt already saved — use "Post" to commit or edit inspection on each line.');
            } else {
                const payload = { ...form };
                const res = await axiosInstance.post('/api/finance/inventory/receipts', payload);
                if (res.data?.success) {
                    navigate(`/finance-portal/inventory/receiving/${res.data.data.id}`);
                }
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const saveInspection = async (i) => {
        if (!editing) return;
        const ln = form.lines[i];
        try {
            await axiosInstance.put(
                `/api/finance/inventory/receipts/${receiptId}/lines/${ln.id}/inspect`,
                {
                    inspection_result: ln.inspection_result,
                    accepted_qty: ln.accepted_qty,
                    rejected_qty: ln.rejected_qty,
                    on_hold_qty: ln.on_hold_qty,
                    inspection_notes: ln.inspection_notes,
                    rejection_reason: ln.rejection_reason,
                },
            );
            setInfo(`Line ${i + 1} inspection saved`);
            load();
        } catch (e) {
            setError(e.response?.data?.message || 'Inspection save failed');
        }
    };

    const postReceipt = async () => {
        if (!editing) return;
        if (!window.confirm('Post this receipt? This creates movements, cost layers and posts to GL.')) return;
        setPosting(true);
        try {
            const res = await axiosInstance.post(`/api/finance/inventory/receipts/${receiptId}/post`);
            if (res.data?.success) {
                setInfo(`Posted. Journal entry #${res.data.journal_entry_id || '—'}`);
                load();
            }
        } catch (e) {
            setError(e.response?.data?.message || 'Post failed');
        } finally {
            setPosting(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>;
    }

    const isPosted = editing && form.status === 'POSTED';

    return (
        <Box sx={{ p: 3, fontFamily: FONT_FAMILY, bgcolor: '#f8fafc', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <IconButton onClick={() => navigate('/finance-portal/inventory/receiving')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '1.55rem', fontWeight: 700, color: '#0f172a' }}>
                                {editing ? form.receipt_number : 'New Receipt'}
                            </Typography>
                            {editing && (
                                <Chip size="small" label={form.status}
                                    style={statusChip(form.status, RECEIPT_STATUS_COLORS)}
                                    sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} />
                            )}
                        </Stack>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                    {!editing && (
                        <Button variant="contained" startIcon={<SaveIcon />} disabled={saving} onClick={handleSave}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none',
                                  bgcolor: INV_ACCENT, '&:hover': { bgcolor: INV_ACCENT_DARK } }}>
                            {saving ? 'Saving…' : 'Save Draft'}
                        </Button>
                    )}
                    {editing && !isPosted && (
                        <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
                            disabled={posting} onClick={postReceipt}
                            sx={{ fontFamily: FONT_FAMILY, textTransform: 'none' }}>
                            {posting ? 'Posting…' : 'Post Receipt'}
                        </Button>
                    )}
                </Stack>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {info && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                    Receipt Info
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" options={pos}
                            getOptionLabel={(o) => `${o.po_number} — ${o.vendor_name}`}
                            value={pos.find((p) => p.id === form.purchase_order_id) || null}
                            disabled={editing}
                            onChange={(e, v) => {
                                set('purchase_order_id', v?.id || null);
                                set('vendor_id', v?.vendor_id || form.vendor_id);
                            }}
                            renderInput={(params) => <TextField {...params} label="Purchase Order" />} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Autocomplete size="small" options={vendors}
                            getOptionLabel={(o) => o.display_name || o.company_name || o.vendor_number || ''}
                            value={vendors.find((v) => v.id === form.vendor_id) || null}
                            disabled={editing}
                            onChange={(e, v) => set('vendor_id', v?.id || null)}
                            renderInput={(params) => <TextField {...params} label="Vendor" required />} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Received At</InputLabel>
                            <Select value={form.received_at_location_id || ''} label="Received At"
                                disabled={editing}
                                onChange={(e) => set('received_at_location_id', e.target.value)}>
                                {locations.filter((l) => l.is_receivable_to).map((l) => (
                                    <MenuItem key={l.id} value={l.id}>{l.code} — {l.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth type="date" label="Receipt Date"
                            disabled={editing}
                            value={form.receipt_date || ''} onChange={(e) => set('receipt_date', e.target.value)}
                            InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Packing Slip #"
                            value={form.vendor_packing_slip_number || ''}
                            disabled={editing && isPosted}
                            onChange={(e) => set('vendor_packing_slip_number', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Vendor Invoice #"
                            value={form.vendor_invoice_number || ''}
                            disabled={editing && isPosted}
                            onChange={(e) => set('vendor_invoice_number', e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField size="small" fullWidth label="Tracking #"
                            value={form.tracking_number || ''}
                            disabled={editing && isPosted}
                            onChange={(e) => set('tracking_number', e.target.value)} />
                    </Grid>
                </Grid>
            </Paper>

            <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, fontSize: '1.05rem', mb: 2 }}>
                    Line Items &amp; Inspection
                </Typography>
                {form.lines.length === 0 ? (
                    <Typography sx={{ fontFamily: FONT_FAMILY, color: '#64748b', textAlign: 'center', py: 3 }}>
                        Select a PO above to pre-fill line items.
                    </Typography>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>SKU</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Expected</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Received</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Accepted</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">Rejected</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }} align="right">On Hold</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Inspection</TableCell>
                                <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>Lot #</TableCell>
                                {editing && !isPosted && <TableCell />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {form.lines.map((ln, i) => (
                                <TableRow key={i}>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY, fontWeight: 600 }}>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.85rem', fontWeight: 600 }}>
                                            {ln.product_sku || ln.sku}
                                        </Typography>
                                        <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: '0.7rem', color: '#64748b' }}>
                                            {ln.product_name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: FONT_FAMILY }} align="right">{ln.expected_qty}</TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 70 }}
                                            disabled={isPosted}
                                            value={ln.received_qty} onChange={(e) => updateLine(i, 'received_qty', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 70 }}
                                            disabled={isPosted}
                                            value={ln.accepted_qty} onChange={(e) => updateLine(i, 'accepted_qty', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 70 }}
                                            disabled={isPosted}
                                            value={ln.rejected_qty} onChange={(e) => updateLine(i, 'rejected_qty', e.target.value)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <TextField size="small" type="number" sx={{ width: 70 }}
                                            disabled={isPosted}
                                            value={ln.on_hold_qty} onChange={(e) => updateLine(i, 'on_hold_qty', e.target.value)} />
                                    </TableCell>
                                    <TableCell>
                                        <Select size="small" sx={{ minWidth: 130 }}
                                            disabled={isPosted}
                                            value={ln.inspection_result || 'NOT_INSPECTED'}
                                            onChange={(e) => updateLine(i, 'inspection_result', e.target.value)}>
                                            {INSPECTION_RESULTS.map((r) => <MenuItem key={r} value={r}>{r.replace('_', ' ')}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <TextField size="small" sx={{ width: 110 }}
                                            disabled={isPosted}
                                            value={ln.lot_number || ''}
                                            onChange={(e) => updateLine(i, 'lot_number', e.target.value)} />
                                    </TableCell>
                                    {editing && !isPosted && (
                                        <TableCell>
                                            <Button size="small" onClick={() => saveInspection(i)}
                                                sx={{ fontFamily: FONT_FAMILY, textTransform: 'none', color: INV_ACCENT }}>
                                                Save
                                            </Button>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Paper>
        </Box>
    );
}
